/**
 * Prompt 22 item 2c — duplicate devices: HARD NO.
 *
 * Shared rendering for the server's DUPLICATE_SERIAL / POSSIBLE_DUPLICATE
 * 409s: instead of a bare toast, create dialogs show the existing device
 * with [View it] and — for the overridable serial-less warning — a
 * [Create anyway] escape hatch (twins with unknown serials do exist).
 */
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";

export interface ConflictExistingAsset {
    id: string;
    name: string | null;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    size_label: string | null;
    location_description: string | null;
}

export interface DeviceConflict {
    /** duplicate = hard block (serial exists); possible = overridable serial-less lookalike. */
    kind: "duplicate" | "possible";
    message: string;
    existingAsset: ConflictExistingAsset | null;
}

/** Returns the structured conflict when the error is one of the 2c 409s, else null. */
export function parseDeviceConflict(e: unknown): DeviceConflict | null {
    if (!(e instanceof ApiError) || e.status !== 409) return null;
    const body = e.body as { error?: string; message?: string; existingAsset?: ConflictExistingAsset } | undefined;
    if (body?.error === "DUPLICATE_SERIAL") {
        return {
            kind: "duplicate",
            message: body.message ?? "This serial already exists at this site.",
            existingAsset: body.existingAsset ?? null,
        };
    }
    if (body?.error === "POSSIBLE_DUPLICATE") {
        return {
            kind: "possible",
            message: body.message ?? "Possible duplicate of an existing device at this site.",
            existingAsset: body.existingAsset ?? null,
        };
    }
    return null;
}

export function describeConflictAsset(a: ConflictExistingAsset): string {
    const head = [a.size_label, a.manufacturer, a.model].filter(Boolean).join(" ") || a.name || "Device";
    const parts = [head];
    if (a.serial_number) parts.push(`SN ${a.serial_number}`);
    if (a.location_description) parts.push(a.location_description);
    return parts.join(" · ");
}

export function DeviceConflictPanel({
    conflict,
    onUseExisting,
    onCreateAnyway,
    createAnywayPending,
}: {
    conflict: DeviceConflict;
    /** Optional: contexts that can "adopt" the existing device (e.g. link it to the job). */
    onUseExisting?: (asset: ConflictExistingAsset) => void;
    /** Only offered for the overridable serial-less warning. */
    onCreateAnyway?: () => void;
    createAnywayPending?: boolean;
}) {
    const hard = conflict.kind === "duplicate";
    const asset = conflict.existingAsset;
    return (
        <div
            className={`rounded-lg border p-3 text-sm ${
                hard
                    ? "border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
                    : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
            }`}
        >
            <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1 space-y-2">
                    <p className="font-medium">
                        {hard ? "This serial already exists at this site" : "Possible duplicate"}
                    </p>
                    {asset ? (
                        <p className="wrap-break-word">{describeConflictAsset(asset)}</p>
                    ) : (
                        <p className="wrap-break-word">{conflict.message}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                        {asset && onUseExisting && (
                            <Button type="button" size="sm" variant="outline" onClick={() => onUseExisting(asset)}>
                                Use existing device
                            </Button>
                        )}
                        {asset && (
                            <Button asChild type="button" size="sm" variant="outline">
                                {/* G5: same-tab (drafts survive navigation); cmd/ctrl+click
                                    still opens a new tab. */}
                                <Link to={`/assets/${asset.id}`}>
                                    View it
                                    <ExternalLink className="ml-1 h-3 w-3" />
                                </Link>
                            </Button>
                        )}
                        {!hard && onCreateAnyway && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={createAnywayPending}
                                onClick={onCreateAnyway}
                            >
                                {createAnywayPending ? "Creating…" : "Create anyway (real twin)"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
